import React from "react";
import { Helmet } from "react-helmet-async";
import { IMAGES } from "../../content/images";

const SITE_URL = "https://quscap.com";
const SITE_NAME = "Quantum US Capital Partners LLC";
const DEFAULT_TITLE = "Quantum US Capital Partners LLC — The Art of Capital";
const DEFAULT_DESCRIPTION =
  "A privately held investment institution. Long-term capital across industrials, infrastructure, energy, and manufacturing.";
const DEFAULT_OG_IMAGE = IMAGES.home_hero_promenade;

/**
 * SeoHead — per-page <head> overrides via react-helmet-async.
 *
 * Props:
 *   title            (string)  — short page title; brand suffix is appended automatically
 *   description      (string)
 *   path             (string)  — pathname for canonical (e.g. "/firm")
 *   ogImage          (string)
 *   ogType           (string)  — "website" (default) or "article"
 *   noIndex          (bool)
 *   structuredData   (object | object[]) — JSON-LD payload(s)
 *   children         (ReactNode) — additional meta if needed
 */
export const SeoHead = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  noIndex = false,
  structuredData,
  children,
  siteWide = false,
}) => {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : DEFAULT_TITLE;
  const canonical = `${SITE_URL}${path}`;

  const jsonLdArray = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  // Site-wide variant only emits schema + og:site_name so page-level
  // SeoHead can own title/description/canonical/robots without duplicates.
  if (siteWide) {
    return (
      <Helmet>
        <meta property="og:site_name" content={SITE_NAME} />
        {jsonLdArray.map((data, idx) => (
          <script
            key={`sw-${idx}`}
            type="application/ld+json"
          >
            {JSON.stringify(data)}
          </script>
        ))}
        {children}
      </Helmet>
    );
  }

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {noIndex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow" />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content="Quantum US Capital Partners" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLdArray.map((data, idx) => (
        <script
          key={idx}
          type="application/ld+json"
        >
          {JSON.stringify(data)}
        </script>
      ))}

      {children}
    </Helmet>
  );
};

export default SeoHead;
